import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Save, Loader, Trash2 } from 'lucide-react';
import { scoutsApi } from '../services/api';
import { AutonomousSection } from '../components/scout/AutonomousSection';
import { TeleopSection } from '../components/scout/TeleopSection';
import { EndgameSection } from '../components/scout/EndgameSection';
import { PenaltiesSection } from '../components/scout/PenaltiesSection';
import { QualitativeSection } from '../components/scout/QualitativeSection';

const initialFormData = {
  event_name: '',
  match_number: 0,
  team_number: 0,
  alliance_color: 'Red' as 'Red' | 'Blue',
  station_position: 1,
  autonomous: {
    auto_mobility: false,
    auto_fuel_scored: 0,
    auto_tower_level: 'None',
    auto_missed_attempts: 0,
  },
  teleop: {
    teleop_fuel_scored: 0,
    average_cycle_time: 0,
    teleop_tower_level: 'None',
    teleop_missed_attempts: 0,
    defense_played_level: 'None',
    was_defended_level: 'None',
  },
  endgame: {
    endgame_tower_level: 'None',
    endgame_success: false,
    endgame_time_seconds: 0,
  },
  penalties: {
    fouls: 0,
    tech_fouls: 0,
    yellow_card: false,
    red_card: false,
  },
  qualitative: {
    driver_skill_rating: 3,
    defense_capability_rating: 3,
    reliability_rating: 3,
    strengths: '',
    weaknesses: '',
    mechanical_issues: '',
    notes: '',
  },
};

export const ScoutFormPage: React.FC = () => {
  const topRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState(initialFormData);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [autoSaveEnabled] = useState(true);

  // Auto-save to localStorage
  useEffect(() => {
    const saveTimer = setTimeout(() => {
      if (autoSaveEnabled) {
        localStorage.setItem('scoutFormDraft', JSON.stringify(formData));
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2000);
      }
    }, 500);

    return () => clearTimeout(saveTimer);
  }, [formData, autoSaveEnabled]);

  // Load draft on mount
  useEffect(() => {
    const draft = localStorage.getItem('scoutFormDraft');
    if (draft) {
      try {
        setFormData(JSON.parse(draft));
      } catch (e) {
        console.error('Failed to load draft');
      }
    }
  }, []);

  const clearAllData = () => {
    if (window.confirm('Are you sure you want to clear all data? This cannot be undone.')) {
      setFormData(initialFormData);
      localStorage.removeItem('scoutFormDraft');
      setSaveStatus('idle');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSaveStatus('saving');

    try {
      await scoutsApi.submitScout(formData);
      setSaveStatus('saved');
      localStorage.removeItem('scoutFormDraft');

      // Clear form and reset
      setFormData({
        ...initialFormData,
        match_number: formData.match_number + 1,
      });

      // Scroll to top
      topRef.current?.scrollIntoView();
    } catch (error) {
      setSaveStatus('error');
      alert('✗ Error submitting scout data');
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div ref={topRef}>
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <div className="glass p-6">
          <h1 className="section-title">Scout Match Data</h1>
          <p className="text-gray-400">Enter detailed scouting information for teams during matches</p>
        </div>
      </motion.div>

      {/* Form */}
      <motion.form onSubmit={handleSubmit} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        {/* Match Info */}
        <motion.div className="glass p-6 mb-4" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 20 }}>
          <h2 className="subsection-title">Match Information</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="form-label">Event Name</label>
              <input
                type="text"
                value={formData.event_name}
                onChange={(e) => setFormData({ ...formData, event_name: e.target.value })}
                placeholder="Event Name"
                className="form-input"
              />
            </div>
            <div>
              <label className="form-label">Match #</label>
              <input
                type="number"
                value={formData.match_number}
                onChange={(e) => setFormData({ ...formData, match_number: parseInt(e.target.value) })}
                placeholder="Match Number"
                className="form-input"
              />
            </div>
            <div>
              <label className="form-label">Team #</label>
              <input
                type="number"
                value={formData.team_number}
                onChange={(e) => setFormData({ ...formData, team_number: parseInt(e.target.value) })}
                placeholder="Team Number"
                className="form-input"
              />
            </div>
            <div>
              <label className="form-label">Alliance</label>
              <select
                value={formData.alliance_color}
                onChange={(e) => setFormData({ ...formData, alliance_color: e.target.value as 'Red' | 'Blue' })}
                className="form-input"
              >
                <option value="Red">Red</option>
                <option value="Blue">Blue</option>
              </select>
            </div>
          </div>
        </motion.div>

        {/* Sections */}
        <AutonomousSection value={formData.autonomous} onChange={(autonomous) => setFormData({ ...formData, autonomous })} />
        <TeleopSection value={formData.teleop} onChange={(teleop) => setFormData({ ...formData, teleop })} />
        <EndgameSection value={formData.endgame} onChange={(endgame) => setFormData({ ...formData, endgame })} />
        <PenaltiesSection value={formData.penalties} onChange={(penalties) => setFormData({ ...formData, penalties })} />
        <QualitativeSection value={formData.qualitative} onChange={(qualitative) => setFormData({ ...formData, qualitative })} />

        {/* Submit Bar */}
        <div className="fixed bottom-0 left-0 right-0 md:static glass p-4 flex gap-3 justify-end">
          <div className="flex items-center gap-2 text-xs text-gray-400">
            {saveStatus === 'saved' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-green-400">
                ✓ Draft saved
              </motion.div>
            )}
            {saveStatus === 'saving' && <Loader className="w-4 h-4 animate-spin" />}
          </div>
          <motion.button
            type="button"
            onClick={clearAllData}
            disabled={isSubmitting}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="btn bg-red-600 hover:bg-red-700 text-white btn-lg flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Clear All
          </motion.button>
          <motion.button
            type="submit"
            disabled={isSubmitting}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="btn btn-primary btn-lg flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {isSubmitting ? 'Submitting...' : 'Submit Scout Data'}
          </motion.button>
        </div>
      </motion.form>
    </div>
  );
};
